import * as React from 'react';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { PaginationButton, PaginationButtonIconPosition } from './PaginationButton.js';
import { Bem, addClass } from '../../utils/css.js';
import '../../constants.js';
import { useId } from '../../utils/hooks/useId.js';
import './Pagination.scss.js';

const NON_ELLIPSIS_PAGINATION_PAGE_LIMIT = 8;
const MIDDLE_BUTTON_COUNT_WITH_ELLIPSES = 4;
const ELLIPSIS_PAGE_SPILL = 4;
const createRange = (from, to) => {
  const range = [];
  for (let i = from; i <= to; i++) {
    range.push(i);
  }
  return range;
};
const bem = Bem.create("vai-pagination");
const Pagination = ({
  className,
  htmlId,
  dataTa = "vai-pagination",
  totalItems,
  itemsPerPage,
  currentPage = 1,
  textPrevious = "Previous",
  textNext = "Next",
  onPageChange
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const id = useId(htmlId, ["pagination"]).toString();
  const renderButton = (text, onClick, isEllipsis, ellipsisPosition) => /* @__PURE__ */ React.createElement("li", {
    key: isEllipsis ? `vai-page-ellipsis-${ellipsisPosition}` : `vai-page-${text}`,
    className: bem.element("list-item")()
  }, /* @__PURE__ */ React.createElement(PaginationButton, {
    disabled: isEllipsis,
    selected: currentPage === text,
    onClick,
    text,
    dataTa: isEllipsis ? "vai-ellipsis" : `vai-page-${text}`
  }));
  const renderPagination = () => {
    const pages = createRange(1, totalPages);
    if (pages.length <= NON_ELLIPSIS_PAGINATION_PAGE_LIMIT) {
      return pages.map((i) => renderButton(i, () => onPageChange(i)));
    }
    const buttons = [];
    const hasLeftEllipsis = currentPage > ELLIPSIS_PAGE_SPILL;
    const hasRightEllipsis = totalPages - currentPage >= ELLIPSIS_PAGE_SPILL;
    const middleButtonCount = hasLeftEllipsis && hasRightEllipsis ? MIDDLE_BUTTON_COUNT_WITH_ELLIPSES : MIDDLE_BUTTON_COUNT_WITH_ELLIPSES + 1;
    buttons.push(renderButton(1, () => onPageChange(1)));
    if (hasLeftEllipsis) {
      buttons.push(renderButton("...", void 0, true, "left"));
    }
    if (!hasLeftEllipsis) {
      for (let i = 0; i < middleButtonCount; i++) {
        const page = i + 2;
        buttons.push(renderButton(page, () => onPageChange(page)));
      }
    } else if (!hasRightEllipsis) {
      for (let i = totalPages - middleButtonCount; i < totalPages; i++) {
        buttons.push(renderButton(i, () => onPageChange(i)));
      }
    } else {
      for (let i = currentPage - 1; i < currentPage + middleButtonCount - 1; i++) {
        buttons.push(renderButton(i, () => onPageChange(i)));
      }
    }
    if (hasRightEllipsis) {
      buttons.push(renderButton("...", void 0, true, "right"));
    }
    buttons.push(renderButton(totalPages, () => onPageChange(totalPages)));
    return buttons;
  };
  return /* @__PURE__ */ React.createElement("nav", {
    id,
    className: addClass(bem.block(), className),
    role: "navigation",
    "aria-label": "Pagination",
    "data-ta": dataTa
  }, /* @__PURE__ */ React.createElement("ul", {
    className: bem.element("list")()
  }, /* @__PURE__ */ React.createElement("li", {
    className: bem.element("list-item")()
  }, /* @__PURE__ */ React.createElement(PaginationButton, {
    disabled: currentPage === 1,
    selected: false,
    onClick: () => onPageChange(currentPage - 1),
    iconName: VaiIcon.ChevronLeft,
    iconPosition: PaginationButtonIconPosition.LEFT,
    text: textPrevious,
    dataTa: "vai-pagination-button-prev"
  })), renderPagination(), /* @__PURE__ */ React.createElement("li", {
    className: bem.element("list-item")()
  }, /* @__PURE__ */ React.createElement(PaginationButton, {
    disabled: currentPage === totalPages,
    selected: false,
    onClick: () => onPageChange(currentPage + 1),
    iconName: VaiIcon.ChevronRight,
    iconPosition: PaginationButtonIconPosition.RIGHT,
    text: textNext,
    dataTa: "vai-pagination-button-next"
  }))));
};

export { Pagination, Pagination as default };
